import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/PrintOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CircularProgress from "@mui/material/CircularProgress";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";

import useSmallGroupData from "layouts/tables/data/smallGroupTableData";

const MEETING_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function SmallGroups() {
  const {
    columns,
    rows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    membersModalOpen,
    setMembersModalOpen,
    attendanceModalOpen,
    setAttendanceModalOpen,
    selectedGroup,
    form,
    setForm,
    handleAdd,
    handleSubmit,
    handleDeleteConfirm,
    isSaving,
    leaderSearch,
    setLeaderSearch,
    leaderResults,
    handleSelectLeader,
    groupMembers,
    memberSearch,
    setMemberSearch,
    memberResults,
    isLoadingMembers,
    handleAddGroupMember,
    handleRemoveGroupMember,
    attendanceRows,
    isLoadingAttendance,
    handleCheckInMember,
  } = useSmallGroupData();

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading small groups...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Small Groups"
        subtitle="Manage cell groups / home groups, their leaders, and weekly attendance."
        actions={
          <>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
              New Group
            </Button>
            <IconButton onClick={handlePrint} sx={{ border: 1, borderColor: "divider" }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </>
        }
      />

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted
          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
          showTotalEntries
          canSearch
          searchLabel="Search small groups..."
        />
      </Card>

      {/* Create / Edit group */}
      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={form.id ? "Edit Small Group" : "New Small Group"}
        onSave={handleSubmit}
        saveLabel={isSaving ? "Saving..." : form.id ? "Save Changes" : "Create Group"}
        saveDisabled={!form.name.trim() || isSaving}
        loading={isSaving}
        maxWidth="sm"
      >
        <FormSection>
          <TextField
            label="Group Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <Box sx={{ position: "relative" }}>
            <TextField
              label="Leader"
              placeholder="Search member..."
              value={leaderSearch}
              onChange={(e) => {
                setLeaderSearch(e.target.value);
                setForm((prev) => ({ ...prev, leader_member_id: null }));
              }}
              fullWidth
              margin="normal"
            />
            {leaderResults.length > 0 && (
              <Card
                sx={{
                  position: "absolute",
                  zIndex: 10,
                  width: "100%",
                  mt: -1,
                  maxHeight: 220,
                  overflowY: "auto",
                }}
              >
                {leaderResults.map((m) => (
                  <Box
                    key={m.id}
                    onClick={() => handleSelectLeader(m)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 2,
                      py: 1,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
                      {m.first_name?.[0]}
                    </Avatar>
                    <Typography variant="body2">
                      {m.first_name} {m.surname}
                    </Typography>
                  </Box>
                ))}
              </Card>
            )}
          </Box>
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
        </FormSection>

        <FormSection>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              select
              label="Meeting Day"
              value={form.meeting_day}
              onChange={(e) => setForm({ ...form, meeting_day: e.target.value })}
              fullWidth
              margin="normal"
            >
              <MenuItem value="">—</MenuItem>
              {MEETING_DAYS.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Meeting Time"
              type="time"
              value={form.meeting_time}
              onChange={(e) => setForm({ ...form, meeting_time: e.target.value })}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <TextField
            label="Location"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            select
            label="Status"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            fullWidth
            margin="normal"
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </FormSection>
      </FormDialog>

      {/* Members */}
      <FormDialog
        open={membersModalOpen}
        onClose={() => setMembersModalOpen(false)}
        title={`Members — ${selectedGroup?.name || ""}`}
        onSave={() => setMembersModalOpen(false)}
        saveLabel="Done"
        maxWidth="sm"
      >
        <FormSection>
          <Box sx={{ position: "relative" }}>
            <TextField
              label="Add a member"
              placeholder="Search by name or phone..."
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              fullWidth
              margin="normal"
            />
            {memberResults.length > 0 && (
              <Card
                sx={{
                  position: "absolute",
                  zIndex: 10,
                  width: "100%",
                  mt: -1,
                  maxHeight: 220,
                  overflowY: "auto",
                }}
              >
                {memberResults.map((m) => (
                  <Box
                    key={m.id}
                    onClick={() => handleAddGroupMember(m)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 2,
                      py: 1,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
                      {m.first_name?.[0]}
                    </Avatar>
                    <Typography variant="body2">
                      {m.first_name} {m.surname}
                    </Typography>
                  </Box>
                ))}
              </Card>
            )}
          </Box>
        </FormSection>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Current members ({groupMembers.length})
        </Typography>
        {isLoadingMembers ? (
          <CircularProgress size={24} />
        ) : groupMembers.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No members yet.
          </Typography>
        ) : (
          groupMembers.map((m) => (
            <Box
              key={m.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 1,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar sx={{ width: 30, height: 30, fontSize: 13 }}>{m.first_name?.[0]}</Avatar>
                <Typography variant="body2" fontWeight={600}>
                  {m.first_name} {m.surname}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => handleRemoveGroupMember(m.id)}>
                <DeleteOutlineIcon fontSize="small" color="error" />
              </IconButton>
            </Box>
          ))
        )}
      </FormDialog>

      {/* Attendance */}
      <FormDialog
        open={attendanceModalOpen}
        onClose={() => setAttendanceModalOpen(false)}
        title={`Attendance — ${selectedGroup?.name || ""}`}
        onSave={() => setAttendanceModalOpen(false)}
        saveLabel="Done"
        maxWidth="sm"
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Mark present
        </Typography>
        {groupMembers.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add members to this group first.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {groupMembers.map((m) => (
              <Button
                key={m.id}
                size="small"
                variant="outlined"
                onClick={() => handleCheckInMember(m.id)}
              >
                {m.first_name} {m.surname}
              </Button>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Recent attendance ({attendanceRows.length})
        </Typography>
        {isLoadingAttendance ? (
          <CircularProgress size={24} />
        ) : attendanceRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No attendance recorded yet.
          </Typography>
        ) : (
          attendanceRows.slice(0, 15).map((a) => (
            <Box
              key={a.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 0.75,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="body2">
                {a.member ? `${a.member.first_name} ${a.member.surname}` : "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(a.checked_in_at).toLocaleString()}
              </Typography>
            </Box>
          ))
        )}
      </FormDialog>

      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedGroup?.name}"?`}
        confirmLabel="Delete"
      />
    </Box>
  );
}

export default SmallGroups;
